import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../Components/ToastNotifications';
import api from '../../utils/api';
import BumdesVECISummary from '../../Components/SurveyVECI/BumdesVECISummary';
import BumdesStepCards from '../../Components/SurveyVECI/BumdesStepCards';

const BumdesHome = () => {
  const navigate = useNavigate();
  const { authUser, refreshUser } = useAuth();
  const [bumdesData, setBumdesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (refreshUser) {
      refreshUser();
    }
  }, []);

  useEffect(() => {
    const fetchBumdesData = async () => {
      try {
        const response = await api.getBumdesPreSurveyAutofill();
        if (response?.data) {
          setBumdesData({
            ...response.data,
            status: authUser?.activeSession?.status || 'Menunggu Persetujuan',
            approvedPath: authUser?.activeSession?.approvedPath,
          });
        }
      } catch (error) {
        console.error('Failed to fetch bumdes data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (authUser) {
      fetchBumdesData();
    }
  }, [authUser]);

  // Check completion states from metadata and localStorage
  const isPreSurveyDone = !!authUser?.activeSession?.isPreSurveyDone;
  const isVeciDone = !!authUser?.activeSession?.isSurveyVeciDone;
  const isClassified = !!authUser?.activeSession?.isClassified;
  const isProgram = !!authUser?.activeSession?.isProgram;
  const rawStatus = authUser?.activeSession?.status || 'PENDING';

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center min-vh-100'>
        <div
          className='spinner-border text-primary'
          role='status'
          style={{ width: '3rem', height: '3rem' }}
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='card card-body border-0 shadow-sm rounded-4 bg-white p-4'>
      {/* Header Banner */}
      <div
        className='card border-0 rounded-4 overflow-hidden mb-4 p-4 shadow-sm text-white position-relative'
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        }}
      >
        <div className='row align-items-center'>
          <div
            className='col-lg-8 position-relative'
            style={{ zIndex: 1 }}
          >
            <div className='d-flex flex-wrap gap-2 mb-3'>
              <span
                className={`badge bg-white ${rawStatus === 'APPROVED' ? 'text-success' : rawStatus === 'REJECTED' ? 'text-danger' : 'text-warning'} px-3 py-2 rounded-pill fw-bold extra-small uppercase-tracking shadow-sm d-inline-block`}
              >
                <i
                  className={`bi ${rawStatus === 'APPROVED' ? 'bi-check-circle-fill text-success' : rawStatus === 'REJECTED' ? 'bi-x-circle-fill text-danger' : 'bi-patch-check-fill text-warning'} me-1`}
                ></i>{' '}
                {rawStatus === 'PENDING'
                  ? 'Terdaftar'
                  : rawStatus === 'APPROVED'
                    ? 'Telah Dievaluasi'
                    : 'Ditolak'}
              </span>
              {isClassified && rawStatus !== 'REJECTED' && (
                <span
                  className={`badge bg-white ${isProgram ? 'text-primary' : 'text-info'} px-3 py-2 rounded-pill fw-bold extra-small uppercase-tracking shadow-sm d-inline-block`}
                >
                  <i className='bi bi-bezier2 me-1'></i>{' '}
                  {isProgram ? 'Jalur Program' : 'Jalur Non-Program'}
                </span>
              )}
            </div>
            <h1 className='fw-bold display-6 mb-2'>{bumdesData?.bumdesName}</h1>
            <p
              className='lead text-white text-opacity-90 mb-3'
              style={{ fontSize: '1rem' }}
            >
              Selamat datang di portal khusus BUMDes. Lakukan evaluasi mandiri
              dan isi instrumen pengembangan secara berkala.
            </p>
            <div className='d-flex flex-wrap gap-3 text-white text-opacity-80 small'>
              <div>
                <i className='bi bi-tag-fill me-1'></i> {bumdesData?.bumdesType}
              </div>
              <div>
                <i className='bi bi-geo-alt-fill me-1'></i>{' '}
                {[
                  bumdesData?.domicilies?.village?.name,
                  bumdesData?.domicilies?.district?.name
                    ? `Kec. ${bumdesData.domicilies.district.name}`
                    : null,
                  bumdesData?.domicilies?.regency?.name,
                  bumdesData?.domicilies?.province?.name,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </div>
              <div>
                <i className='bi bi-calendar-event-fill me-1'></i> Berdiri:{' '}
                {bumdesData?.establishmentYear}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Cards */}
      <BumdesStepCards
        isPreSurveyDone={isPreSurveyDone}
        isVeciDone={isVeciDone}
        isClassified={isClassified}
        isProgram={isProgram}
      />

      {/* VECI T0 Summary for BUMDes */}
      {isVeciDone && <BumdesVECISummary />}

      {/* Info Card / Pedoman */}
      <div className='card border-0 shadow-sm rounded-4 p-4 bg-white'>
        <h5 className='fw-bold text-dark mb-3'>
          <i className='bi bi-info-circle-fill text-primary me-2'></i>Panduan &
          Langkah Berikutnya
        </h5>
        <div className='row g-4'>
          <div className='col-md-6'>
            <div className='d-flex gap-3'>
              <div className='bg-light p-2 rounded-3 text-primary h-100'>
                <i className='bi bi-1-circle-fill fs-5'></i>
              </div>
              <div>
                <h6 className='fw-bold text-dark mb-1'>
                  Isi Pre-Survey BUMDes
                </h6>
                <p className='text-muted small mb-0'>
                  Pastikan seluruh data dasar penanggung jawab dan lembaga
                  BUMDes lengkap.
                </p>
              </div>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='d-flex gap-3'>
              <div className='bg-light p-2 rounded-3 text-primary h-100'>
                <i className='bi bi-2-circle-fill fs-5'></i>
              </div>
              <div>
                <h6 className='fw-bold text-dark mb-1'>Isi Survei VECI</h6>
                <p className='text-muted small mb-0'>
                  Isi kuesioner dengan objektif untuk memetakan kekuatan modal
                  keuangan, fisik, sosial, dan manusia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hidden::-webkit-scrollbar {
          height: 6px;
        }
        .scrollbar-hidden::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.03);
          border-radius: 10px;
        }
        .scrollbar-hidden::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 10px;
        }
        .scrollbar-hidden::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.2);
        }
        .card-hover {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .card-hover:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.05) !important;
        }
      `}</style>
    </div>
  );
};

export default BumdesHome;
